/**
 * CharacterSystem — Singleton for character creation, management, and serialization.
 * All data is JSON-driven from races.json, classes.json, skill_trees.json.
 *
 * @module CharacterSystem
 */

import DebugLog from '../debug/DebugLog.js';
import RACES_DATA from '../data/races.json';
import CLASSES_DATA from '../data/classes.json';
import SKILL_TREES_DATA from '../data/skill_trees.json';
import FEATS_DATA from '../data/feats.json';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateId() {
  return 'char_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now().toString(36);
}

// ─── CharacterSystem ──────────────────────────────────────────────────────────

const CharacterSystem = {
  /** @type {Map<string, object>} In-memory character registry */
  _characters: new Map(),

  /** @type {Array} Cached races array */
  _races: RACES_DATA,

  /** @type {object} Cached classes data */
  _classes: CLASSES_DATA,

  /** @type {Array} Cached skill trees array */
  _skillTrees: SKILL_TREES_DATA,

  // ─── Core Calculations ─────────────────────────────────────────────────────

  /**
   * Compute attribute modifier (D&D formula).
   * @param {number} attrValue
   * @returns {number}
   */
  getAttributeModifier(attrValue) {
    return Math.floor((attrValue - 10) / 2);
  },

  /**
   * Get proficiency bonus for a given level.
   * @param {number} level
   * @returns {number}
   */
  getProficiencyBonus(level) {
    return Math.floor(level / 4) + 1;
  },

  /**
   * Compute all derived ability scores for a character.
   * @param {object} character
   * @returns {object} Key: ability name, value: numeric score
   */
  getDerivedAbilities(character) {
    const attrs = character.attributes;
    const prof = this.getProficiencyBonus(character.level ?? 1);

    const mightMod     = this.getAttributeModifier(attrs.might     ?? 10);
    const agilityMod   = this.getAttributeModifier(attrs.agility   ?? 10);
    const intellectMod = this.getAttributeModifier(attrs.intellect ?? 10);
    const wisdomMod    = this.getAttributeModifier(attrs.wisdom    ?? 10);
    const enduranceMod = this.getAttributeModifier(attrs.endurance ?? 10);
    const presenceMod  = this.getAttributeModifier(attrs.presence  ?? 10);

    return {
      athletics:   mightMod     + prof,
      acrobatics:  agilityMod   + prof,
      stealth:     agilityMod   + prof,
      arcana:      intellectMod + prof,
      history:     intellectMod + prof,
      insight:     wisdomMod    + prof,
      nature:      wisdomMod    + prof,
      perception:  wisdomMod    + prof,
      survival:    wisdomMod    + prof,
      persuasion:  presenceMod  + prof,
      deception:   presenceMod  + prof,
      intimidate:  mightMod     + prof,
      command:     presenceMod  + prof,
      // Expose modifiers for display
      mightMod,
      agilityMod,
      intellectMod,
      wisdomMod,
      enduranceMod,
      presenceMod,
    };
  },

  /**
   * Compute the class name from a set of 3 skill tree IDs.
   * Algorithm: exact match → partial match → fallback by primary tree.
   * @param {string[]} treesArray — Array of tree IDs (typically 3)
   * @returns {string} Class name
   */
  computeClass(treesArray) {
    if (!treesArray || treesArray.length === 0) return 'Adventurer';

    const sorted = [...treesArray].filter(Boolean).sort();
    const key = sorted.join('+');

    // 1. Check exact matches (sorted join)
    for (const entry of this._classes.exact) {
      const entryKey = [...entry.trees].sort().join('+');
      if (entryKey === key) {
        return entry.className;
      }
    }

    // 2. Check partial 2-tree matches
    for (const entry of this._classes.partial) {
      // entry.trees can be 1 or 2 trees; all must be present in treesArray
      if (entry.trees.every(t => treesArray.includes(t))) {
        return entry.className;
      }
    }

    // 3. Fallback: use primary tree (first tree)
    const primaryTree = treesArray[0];
    if (primaryTree && this._classes.fallback[primaryTree]) {
      return this._classes.fallback[primaryTree];
    }

    return 'Adventurer';
  },

  /**
   * Compute max HP for a character (base + endurance-based growth per level).
   * @param {object} character
   * @returns {number}
   */
  computeMaxHP(character) {
    const endMod = this.getAttributeModifier(character.attributes.endurance ?? 10);
    // Base HP: 10 + endurance modifier, then +5 per level after 1 plus endurance modifier
    const base = 10 + Math.max(0, endMod);
    const perLevel = Math.max(1, 5 + endMod);
    return base + (Math.max(1, character.level ?? 1) - 1) * perLevel;
  },

  /**
   * Compute max mana for a character.
   * @param {object} character
   * @returns {number}
   */
  computeMaxMana(character) {
    const intMod = this.getAttributeModifier(character.attributes.intellect ?? 10);
    const base = 10 + Math.max(0, intMod);
    const perLevel = Math.max(1, 4 + intMod);
    return base + (Math.max(1, character.level ?? 1) - 1) * perLevel;
  },

  // ─── Character Creation ────────────────────────────────────────────────────

  /**
   * Create a new hero character from CharCreate options.
   * @param {object} options
   * @param {string} options.name
   * @param {string} options.raceId
   * @param {object} [options.attributeOverrides] — Final attribute values (after race bonuses + free points)
   * @param {object} [options.appearance]
   * @param {object} [options.background]
   * @param {string[]} [options.skillTrees] — Array of 3 tree IDs
   * @param {string} [options.startingFeat]
   * @param {string} [options.alignment]
   * @returns {object} Character data object
   */
  createHero(options) {
    const {
      name = 'Hero',
      raceId = 'human',
      attributeOverrides = null,
      appearance = {},
      background = { childhood: 'farmer', adult: 'soldier' },
      skillTrees = [],
      startingFeat = null,
      alignment = 'neutral',
    } = options;

    const race = this._races.find(r => r.id === raceId);
    if (!race) {
      DebugLog.error(`[party] CharacterSystem.createHero: unknown race "${raceId}"`);
    }

    // Compute final attributes
    const baseAttrs = { ...(race?.baseAttributes ?? { might: 10, agility: 10, intellect: 10, wisdom: 10, endurance: 10, presence: 10 }) };
    const bonuses = race?.attributeBonuses ?? {};
    const attrs = {};
    for (const key of Object.keys(baseAttrs)) {
      attrs[key] = baseAttrs[key] + (bonuses[key] ?? 0);
    }

    // Apply overrides if provided (from CharCreate attribute assignment step)
    if (attributeOverrides) {
      for (const key of Object.keys(attributeOverrides)) {
        if (key in attrs) {
          attrs[key] = attributeOverrides[key];
        }
      }
    }

    const className = this.computeClass(skillTrees);

    const character = {
      id: generateId(),
      name,
      race: raceId,
      level: 1,
      xp: 0,
      alignment,
      class: className,
      background: { ...background },
      appearance: {
        bodyType: appearance.bodyType ?? 1,
        headType: appearance.headType ?? 1,
        skinColor: appearance.skinColor ?? '#c8a882',
        hairColor: appearance.hairColor ?? '#8b4513',
        eyeColor:  appearance.eyeColor  ?? '#2e8b57',
      },
      attributes: attrs,
      skillTrees: [...skillTrees],
      skills: {},
      skillPoints: 3,
      feats: startingFeat ? [startingFeat] : [],
      equipment: {
        weapon: null, offhand: null, armor: null, helmet: null,
        boots: null, gloves: null, ring1: null, ring2: null,
        amulet: null, belt: null,
      },
      inventory: [],
      partySkill: null,
      companions: [],
      attitude: 100,
      wage: 0,
      isHero: true,
    };

    // Compute derived stats
    character.maxHP  = this.computeMaxHP(character);
    character.hp     = character.maxHP;
    character.maxMana = this.computeMaxMana(character);
    character.mana   = character.maxMana;

    this._characters.set(character.id, character);

    DebugLog.info(
      `[party] Hero created; name=${character.name} race=${raceId} ` +
      `class=${className} trees=${skillTrees.join(',')}`
    );

    return character;
  },

  /**
   * Generate a random hired NPC.
   * @param {object} [options]
   * @param {number} [options.level]
   * @param {string} [options.raceId]
   * @returns {object} NPC character data
   */
  createNPC(options = {}) {
    const { level = 1 } = options;

    // Playable non-locked races for NPC pool
    const npcRaces = this._races.filter(r => r.playable && !r.unlockCondition);
    const race = options.raceId
      ? this._races.find(r => r.id === options.raceId) ?? npcRaces[Math.floor(Math.random() * npcRaces.length)]
      : npcRaces[Math.floor(Math.random() * npcRaces.length)];

    // Random attributes spread appropriate for level
    const spread = Math.floor(level * 0.5);
    const baseAttrs = { ...(race.baseAttributes ?? { might: 10, agility: 10, intellect: 10, wisdom: 10, endurance: 10, presence: 10 }) };
    const bonuses = race.attributeBonuses ?? {};
    const attrs = {};
    for (const key of Object.keys(baseAttrs)) {
      const randBonus = Math.floor(Math.random() * (spread + 1));
      attrs[key] = Math.min(20, baseAttrs[key] + (bonuses[key] ?? 0) + randBonus);
    }

    // Pick 2-3 random trees
    const standardTrees = this._skillTrees.filter(t => t.tier === 'standard');
    const shuffled = [...standardTrees].sort(() => Math.random() - 0.5);
    const treesCount = 2 + Math.floor(Math.random() * 2);
    const npcTrees = shuffled.slice(0, treesCount).map(t => t.id);

    const npcNames = ['Bjorn', 'Sigrid', 'Erik', 'Astrid', 'Gunnar', 'Freya', 'Leif', 'Ingrid', 'Ragnar', 'Helga'];
    const name = npcNames[Math.floor(Math.random() * npcNames.length)];

    const className = this.computeClass(npcTrees);

    const character = {
      id: generateId(),
      name,
      race: race.id,
      level,
      xp: 0,
      alignment: 'neutral',
      class: className,
      background: { childhood: 'farmer', adult: 'soldier' },
      appearance: {
        bodyType: 1 + Math.floor(Math.random() * 5),
        headType: 1 + Math.floor(Math.random() * 8),
        skinColor: '#c8a882',
        hairColor: '#8b4513',
        eyeColor:  '#2e8b57',
      },
      attributes: attrs,
      skillTrees: npcTrees,
      skills: {},
      feats: [],
      equipment: {
        weapon: null, offhand: null, armor: null, helmet: null,
        boots: null, gloves: null, ring1: null, ring2: null,
        amulet: null, belt: null,
      },
      inventory: [],
      partySkill: null,
      companions: [],
      attitude: 50 + Math.floor(Math.random() * 30),
      wage: 8 + Math.floor(Math.random() * 12),
      isHero: false,
    };

    character.maxHP   = this.computeMaxHP(character);
    character.hp      = character.maxHP;
    character.maxMana = this.computeMaxMana(character);
    character.mana    = character.maxMana;

    this._characters.set(character.id, character);

    DebugLog.info(
      `[party] NPC created; name=${character.name} race=${race.id} ` +
      `class=${className} wage=${character.wage}`
    );

    return character;
  },

  // ─── Equipment ────────────────────────────────────────────────────────────

  /**
   * Validate whether a character can equip an item.
   * @param {object} character
   * @param {object} item
   * @returns {{ valid: boolean, reason: string|null }}
   */
  validateEquip(character, item) {
    if (!item) return { valid: false, reason: 'No item provided.' };

    // Check attribute requirements
    if (item.requirements?.attributes) {
      for (const [attr, required] of Object.entries(item.requirements.attributes)) {
        if ((character.attributes[attr] ?? 0) < required) {
          return { valid: false, reason: `Requires ${attr} ${required}.` };
        }
      }
    }

    // Check race restrictions
    if (item.requirements?.races && item.requirements.races.length > 0) {
      if (!item.requirements.races.includes(character.race)) {
        return { valid: false, reason: `Race restriction: ${item.requirements.races.join(', ')}.` };
      }
    }

    // Check level requirement
    if (item.requirements?.level && character.level < item.requirements.level) {
      return { valid: false, reason: `Requires level ${item.requirements.level}.` };
    }

    return { valid: true, reason: null };
  },

  // ─── Leveling ─────────────────────────────────────────────────────────────

  /**
   * Apply level-up bonuses to a character.
   * @param {object} character
   * @returns {object} Updated character
   */
  levelUp(character) {
    character.level = (character.level ?? 1) + 1;

    // Recalculate derived HP and mana
    const oldMaxHP   = character.maxHP ?? 0;
    const oldMaxMana = character.maxMana ?? 0;

    character.maxHP   = this.computeMaxHP(character);
    character.maxMana = this.computeMaxMana(character);

    // Heal for the difference gained
    character.hp   = (character.hp   ?? 0) + (character.maxHP   - oldMaxHP);
    character.mana = (character.mana ?? 0) + (character.maxMana - oldMaxMana);

    character.class = this.computeClass(character.skillTrees ?? []);

    DebugLog.info(
      `[party] Level up; name=${character.name} level=${character.level} ` +
      `maxHP=${character.maxHP} maxMana=${character.maxMana}`
    );

    return character;
  },

  /**
   * Total XP required to reach level n (cumulative from level 1).
   * Formula: 100 * n * (n + 1) / 2
   * Examples: level 2 = 300, level 3 = 600, level 4 = 1000, level 5 = 1500.
   * @param {number} n — target level
   * @returns {number} Total XP needed to have reached level n
   */
  xpForLevel(n) {
    if (n <= 1) return 0;
    return 100 * n * (n + 1) / 2;
  },

  /**
   * Grant XP to a character and handle level-ups.
   * Uses cumulative XP threshold: xpForLevel(n) = 100 * n * (n+1) / 2.
   * Level 2 requires 300 total XP, level 3 requires 600, level 4 requires 1000, etc.
   * @param {object} character
   * @param {number} amount
   */
  grantXP(character, amount) {
    character.xp = (character.xp ?? 0) + amount;
    // Check for level-ups using cumulative XP threshold
    let nextLevel = (character.level ?? 1) + 1;
    while (character.xp >= this.xpForLevel(nextLevel)) {
      this.levelUp(character);
      character.skillPoints = (character.skillPoints ?? 0) + 1;
      DebugLog.info(`[party] ${character.name} leveled up to ${character.level}!`);
      nextLevel = character.level + 1;
    }
  },

  // ─── Serialization ────────────────────────────────────────────────────────

  /**
   * Serialize a character for saving.
   * @param {object} character
   * @returns {object} Plain serializable object
   */
  serialize(character) {
    return JSON.parse(JSON.stringify(character));
  },

  /**
   * Deserialize a character from save data.
   * @param {object} data
   * @returns {object} Restored character
   */
  deserialize(data) {
    const character = JSON.parse(JSON.stringify(data));
    // Recompute class in case skill trees changed
    if (character.skillTrees) {
      character.class = this.computeClass(character.skillTrees);
    }
    this._characters.set(character.id, character);
    return character;
  },

  // ─── Queries ──────────────────────────────────────────────────────────────

  /**
   * Get a character by ID.
   * @param {string} id
   * @returns {object|null}
   */
  getById(id) {
    return this._characters.get(id) ?? null;
  },

  /**
   * Get all loaded characters.
   * @returns {object[]}
   */
  getAll() {
    return [...this._characters.values()];
  },

  /**
   * Get race data by ID.
   * @param {string} raceId
   * @returns {object|null}
   */
  getRace(raceId) {
    return this._races.find(r => r.id === raceId) ?? null;
  },

  /**
   * Get all playable races.
   * @returns {object[]}
   */
  getPlayableRaces() {
    return this._races.filter(r => r.playable);
  },

  /**
   * Get all skill trees.
   * @returns {object[]}
   */
  getSkillTrees() {
    return this._skillTrees;
  },

  /**
   * Clear all characters from the in-memory registry.
   */
  clear() {
    this._characters.clear();
  },

  /**
   * Add a feat to a character after validating requirements.
   * @param {object} character
   * @param {string} featId
   * @returns {{ success: boolean, message: string }}
   */
  addFeat(character, featId) {
    const feat = FEATS_DATA.find(f => f.id === featId);
    if (!feat) return { success: false, message: 'Unknown feat.' };
    if (character.feats?.includes(featId)) return { success: false, message: 'Already have this feat.' };
    const reqs = feat.requirements ?? {};
    if ((character.level ?? 1) < (reqs.level ?? 1)) {
      return { success: false, message: `Requires level ${reqs.level}.` };
    }
    for (const [attr, val] of Object.entries(reqs.attributes ?? {})) {
      if ((character.attributes?.[attr] ?? 0) < val) {
        return { success: false, message: `Requires ${attr} ${val}.` };
      }
    }
    if (!character.feats) character.feats = [];
    character.feats.push(featId);
    return { success: true, message: `Feat added: ${feat.name}` };
  },

  /**
   * Return feats the character qualifies for and hasn't taken yet.
   * @param {object} character
   * @returns {object[]}
   */
  getAvailableFeats(character) {
    return FEATS_DATA.filter(feat => {
      if (character.feats?.includes(feat.id)) return false;
      const reqs = feat.requirements ?? {};
      if ((character.level ?? 1) < (reqs.level ?? 1)) return false;
      for (const [attr, val] of Object.entries(reqs.attributes ?? {})) {
        if ((character.attributes?.[attr] ?? 0) < val) return false;
      }
      return true;
    });
  },

  /**
   * How many feats should this character have? (Level 1, then every 5 levels)
   * @param {object} character
   * @returns {number}
   */
  getFeatCount(character) {
    return 1 + Math.floor(((character.level ?? 1) - 1) / 5);
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__debugGetCharacterState = (id) => {
    if (id) return CharacterSystem.getById(id);
    return CharacterSystem.getAll();
  };
}

export default CharacterSystem;
